import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import RideCard from '../components/RideCard';

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
  </svg>
);
const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

export default function BrowseRides() {
  const { rides, currentUser } = useApp();
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [maxPrice, setMaxPrice] = useState('');
  const [sort, setSort] = useState('date');

  const today = new Date().toISOString().split('T')[0];

  const filtered = useMemo(() => {
    return rides
      .filter(r => {
        if (r.status === 'cancelled') return false;
        if (r.driverId === currentUser.id) return false;
        if (r.date < today) return false;
        if (search) {
          const q = search.toLowerCase();
          if (!r.destination.toLowerCase().includes(q) && !r.departure.toLowerCase().includes(q)) return false;
        }
        if (dateFilter && r.date !== dateFilter) return false;
        if (maxPrice && r.price > Number(maxPrice)) return false;
        return true;
      })
      .sort((a, b) => {
        if (sort === 'date') return a.date.localeCompare(b.date) || a.time.localeCompare(b.time);
        if (sort === 'price') return a.price - b.price;
        if (sort === 'seats') return b.seatsAvailable - a.seatsAvailable;
        return 0;
      });
  }, [rides, search, dateFilter, maxPrice, sort, currentUser, today]);

  const hasFilters = dateFilter || maxPrice;

  return (
    <div className="page">
      <div style={{ paddingTop: 8, marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 4 }}>Browse Rides</h1>
        <p style={{ fontSize: 13, color: 'var(--gray-400)' }}>{filtered.length} ride{filtered.length !== 1 ? 's' : ''} available</p>
      </div>

      {/* Search bar */}
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)' }}>
          <SearchIcon />
        </div>
        <input
          className="form-input"
          type="text"
          placeholder="Search destination or departure..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ paddingLeft: 38, paddingRight: search ? 38 : 14 }}
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
              background: 'none', color: 'var(--gray-400)' }}
          >
            <XIcon />
          </button>
        )}
      </div>

      {/* Filters row */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <button
          onClick={() => setShowFilters(s => !s)}
          className={`btn ${showFilters || hasFilters ? 'btn-outline' : 'btn-ghost'}`}
          style={{ padding: '8px 12px', fontSize: 13, gap: 6 }}
        >
          <FilterIcon /> Filters
          {hasFilters && (
            <span style={{
              background: 'var(--accent)', color: 'white',
              borderRadius: '50%', width: 16, height: 16,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700,
            }}>
              {[dateFilter, maxPrice].filter(Boolean).length}
            </span>
          )}
        </button>
        <div style={{ flex: 1 }} />
        <select
          value={sort} onChange={e => setSort(e.target.value)}
          style={{
            background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
            borderRadius: 'var(--radius-sm)', color: 'var(--white)',
            padding: '8px 10px', fontSize: 13,
          }}
        >
          <option value="date">Sort: Soonest</option>
          <option value="price">Sort: Cheapest</option>
          <option value="seats">Sort: Most Seats</option>
        </select>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div style={{
          background: 'var(--navy-light)', border: '1px solid var(--navy-border)',
          borderRadius: 'var(--radius)', padding: '16px', marginBottom: 16,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Date</label>
              <input className="form-input" type="date" min={today}
                value={dateFilter} onChange={e => setDateFilter(e.target.value)}
                style={{ colorScheme: 'dark' }} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Max Price ($)</label>
              <input className="form-input" type="number" min="0" placeholder="Any"
                value={maxPrice} onChange={e => setMaxPrice(e.target.value)} />
            </div>
          </div>
          {hasFilters && (
            <button
              onClick={() => { setDateFilter(''); setMaxPrice(''); }}
              style={{ marginTop: 12, background: 'none', color: 'var(--red)', fontSize: 13, fontWeight: 500 }}
            >
              Clear all filters
            </button>
          )}
        </div>
      )}

      {/* Ride list */}
      {filtered.length === 0 ? (
        <div style={{
          background: 'var(--navy-light)', border: '1px solid var(--navy-border)',
          borderRadius: 'var(--radius)', padding: '40px 24px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>No rides found</p>
          <p style={{ color: 'var(--gray-400)', fontSize: 13 }}>
            {hasFilters || search ? 'Try adjusting your filters.' : 'No rides posted yet. Check back soon!'}
          </p>
          {(hasFilters || search) && (
            <button
              onClick={() => { setSearch(''); setDateFilter(''); setMaxPrice(''); }}
              className="btn btn-ghost"
              style={{ marginTop: 16 }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        filtered.map(r => <RideCard key={r.id} ride={r} />)
      )}
    </div>
  );
}
